import { useTranslation } from 'react-i18next'
import { Button, Icon } from '@atoms'
import { useState, useMemo, useCallback } from 'react'
import { GetReportResponse } from '@/hooks'
import { Dropdown, MenuItemsArray, VerticalMenu } from '@/components'
import { Spinner } from '@/components'
import { ExportReportMenuItem } from './ExportReportMenuItem'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import { useRenameReport } from '@/features/Reporting/hooks/useRenameReport/useRenameReport'
import { useDropdownClickOutside } from '@/components/common/Dropdown/useDropdownClickOutside'

type ReportDetailsActionsDropdownProps = {
    handleClickDeleteReport: () => void
    reportData: GetReportResponse
}

export const ReportDetailsActionsDropdown: React.FC<
    ReportDetailsActionsDropdownProps
> = ({ handleClickDeleteReport, reportData }) => {
    const { t } = useTranslation('translations')
    const [isOpen, setIsOpen] = useState(false)
    const [isExporting, setIsExporting] = useState(false)

    const isIntervalData = reportData.type === 'interval-data'

    const handleExportingChange = useCallback((value: boolean) => {
        setIsExporting(value)
    }, [])

    const { openRenameReportModal } = useRenameReport()

    const { buttonRef, dropdownRef } = useDropdownClickOutside(() => {
        if (isOpen) {
            setIsOpen(false)
        }
    })

    const userId = useAuthStore((state) => state.user?.id) ?? ''
    const isOwner = userId === reportData.owner.userId

    const menuItems = useMemo<MenuItemsArray>(() => {
        const items: MenuItemsArray = [
            {
                id: 'rename',
                path: '/rename',
                label: t('common.rename'),
                isHidden: !isOwner,
                render: ({
                    renderContents,
                }: {
                    renderContents: () => React.ReactNode
                }) => (
                    <button
                        className="group w-full outline-none"
                        onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            setIsOpen(false)
                            openRenameReportModal(reportData)
                        }}
                        data-testid="rename-action-button"
                    >
                        {renderContents()}
                    </button>
                ),
            },
            {
                id: 'export',
                path: '/export',
                label: t('common.export'),
                render: () => (
                    <ExportReportMenuItem
                        reportData={reportData}
                        onClick={() => {
                            setIsOpen(false)
                        }}
                        onExportingChange={
                            isIntervalData ? handleExportingChange : undefined
                        }
                    />
                ),
            },
            {
                id: 'delete',
                path: '/delete',
                label: t('common.delete'),
                isHidden: !isOwner,
                render: ({
                    renderContents,
                }: {
                    renderContents: () => React.ReactNode
                }) => (
                    <button
                        className="group w-full outline-none"
                        onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            setIsOpen(false)
                            handleClickDeleteReport()
                        }}
                        data-testid="delete-action-button"
                    >
                        {renderContents()}
                    </button>
                ),
            },
        ]

        return items
    }, [
        t,
        isOwner,
        reportData,
        openRenameReportModal,
        handleClickDeleteReport,
        isIntervalData,
        handleExportingChange,
    ])

    return (
        <Dropdown
            name="report-details-actions-dropdown"
            dropdownRef={dropdownRef}
            buttonProps={{
                name: 'actions-view-report-dropdown',
                children: (
                    <>
                        {t('reports.actions_report_dropdown.label')}
                        {isExporting ? (
                            <Spinner size={16} width={3} />
                        ) : (
                            <Icon size="small" name="keyboard_arrow_down" />
                        )}
                    </>
                ),
                variant: 'outlinePrimary',
                size: 'large',
                disabled: isExporting,
                buttonRef,
            }}
            buttonComponent={Button}
            horizontalAlign="right"
            dropdownClassname="py-3 !min-w-[auto]"
            isOpen={isOpen}
            onClick={(event) => {
                event.stopPropagation()
                event.preventDefault()
                setIsOpen((current) => !current)
            }}
        >
            <VerticalMenu
                menuItems={menuItems}
                variant={'primary'}
                hideLabel={false}
                menuItemClassName={'w-auto whitespace-normal break-words'}
            />
        </Dropdown>
    )
}
